import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import classNames from 'classnames';

import withModal from '../../../../../components/global/modal/withModal';
import DismissButton from '../../../../../components/global/DismissButton';
import { createCustomPrintCandidatePhotoGroups } from '../../../../../components/global/CustomPrintConfigurator/helpers';
import CardElementPhotoSelectionControlNav from './CardElementPhotoSelectionControlNav';
import CardPhotoSelect from './CardPhotoSelect';
import Button from '../../../../../components/global/Button';
import CardElementPhotoControlAssetUpload from '../CardElementPhotoControlAssetUpload';
import getAccountCardConfigurationPhotosQuery from '../../../graphql/queries/getAccountCardConfigurationPhotos';
import getAccountCardRosterIdentityConfigurationPhotosQuery from '../../../graphql/queries/getAccountCardRosterIdentityConfigurationPhotos';
import useVirtualPageView from '../../../../../apps/pro/services/analytics/useVirtualPageView';
import getAccountCardMembershipGroupConfigurationPhotosQuery from '../../../graphql/queries/getAccountCardMembershipGroupConfigurationPhotos';
import getAccountCardMembershipGroupIdentityConfigurationPhotosQuery from '../../../graphql/queries/getAccountCardMembershipGroupIdentityConfigurationPhotos';
import CardElementPhotoSelectionControlGridSelect, {
  GRID_TYPES,
} from './CardElementPhotoSelectionControlGridSelect';
import getAccountCardAlbumConfigurationPhotosQuery from '../../../graphql/queries/getAccountCardAlbumConfigurationPhotos';
import { PHOTO_SELECTION_CONTROL_FACES_OPTIONS } from './constants';
import { IdentityAlbumFolderPhotos } from '../../../../../graphql/queries/album-folder';

const propTypes = {
  loading: PropTypes.bool,
  defaultPhotoGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      total: PropTypes.number,
      photos: PropTypes.array,
      loadMore: PropTypes.func,
    }),
  ),
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onRecordChange: PropTypes.func,
  photosContext: PropTypes.shape({
    for_collages: PropTypes.bool,
    uploads_disabled: PropTypes.bool,
    grid_select_disabled: PropTypes.bool,
  }),
  uploadDisabled: PropTypes.bool,
  facesOptionDefault: PropTypes.oneOf(
    Object.values(PHOTO_SELECTION_CONTROL_FACES_OPTIONS),
  ),
  maxPhotosCount: PropTypes.number,
  maxPhotosCountStrict: PropTypes.bool,
  title: PropTypes.node,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
    watermarkMode: PropTypes.string,
    wallpaperWatermarkMode: PropTypes.string,
  }),
  allowDismiss: PropTypes.bool,
  teamPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
};

const CardElementPhotoSelectionControl = ({
  loading,
  defaultPhotoGroups = [],
  onClose,
  onChange,
  onRecordChange,
  photosContext,
  uploadDisabled = false,
  facesOptionDefault = PHOTO_SELECTION_CONTROL_FACES_OPTIONS.ALL,
  maxPhotosCount = 1,
  maxPhotosCountStrict = true,
  currentSelectedPhotos,
  currentSelectedPhoto: propsCurrentSelectedPhoto,
  title = 'Pick a photo',
  watermarkSettings,
  allowDismiss = true,
  teamPhotos,
}) => {
  // When teamPhotos are provided, use them as the sole photo source
  // and disable the upload tab.
  const teamPhotoRecords = useMemo(
    () => (teamPhotos ?? []).filter(tp => !!tp?.photo),
    [teamPhotos],
  );
  const hasTeamPhotos = teamPhotoRecords.length > 0;
  const effectiveDefaultPhotoGroups = hasTeamPhotos
    ? [
        {
          id: 'team-photos',
          label: 'Team Photos',
          total: teamPhotoRecords.length,
          photos: teamPhotoRecords,
        },
      ]
    : defaultPhotoGroups;
  const effectiveUploadDisabled = uploadDisabled || hasTeamPhotos;

  // Add the custom upload tab as a "photoGroup"
  // on top of the default ones.
  const photoGroups =
    effectiveUploadDisabled || photosContext?.uploads_disabled
      ? effectiveDefaultPhotoGroups
      : [
          ...effectiveDefaultPhotoGroups,
          ...[
            {
              id: 'custom-upload',
              label: 'Upload',
            },
          ],
        ];
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentSelectedPhoto, setCurrentSelectedPhoto] = useState(
    propsCurrentSelectedPhoto ?? null,
  );
  const [selectedPhotos, setSelectedPhotos] = useState(
    currentSelectedPhotos ?? [],
  );
  const [currentSelectedRecord, setCurrentSelectedRecord] = useState(null);
  const [facesOption, setFacesOption] = useState(facesOptionDefault);
  const [activePhotoGroupIndex, setActivePhotoGroupIndex] = useState(0);
  const [gridType, setGridType] = useState(GRID_TYPES.SINGLE);
  const activePhotoGroup = photoGroups[activePhotoGroupIndex] || {};
  const { photos = [], loadMore, total = 0 } = activePhotoGroup;

  const { onVirtualPageViewTrack } = useVirtualPageView({ fireOnMount: false });

  const hasPhotoWithIndividualFace = useMemo(
    () =>
      !!photos?.find(
        record =>
          record.photo?.faces?.total === 1 || record?.faces?.total === 1,
      ),
    [activePhotoGroup, photos, facesOption],
  );
  const hasPhotoWithMultipleFaces = useMemo(
    () =>
      !!photos?.find(
        record => record.photo?.faces?.total > 1 || record?.faces?.total > 1,
      ),
    [activePhotoGroup, photos, facesOption],
  );

  const availablePhotos = useMemo(() => {
    if (
      facesOption === PHOTO_SELECTION_CONTROL_FACES_OPTIONS.ALL ||
      activePhotoGroup?.id !== 'matches'
    ) {
      return photos;
    }

    if (facesOption === PHOTO_SELECTION_CONTROL_FACES_OPTIONS.INDIVIDUAL) {
      return [...photos].sort((a, b) => {
        return a.photo?.faces?.total - b.photo?.faces?.total;
      });
    }

    return [...photos].sort((a, b) => {
      return b.photo?.faces?.total - a.photo?.faces?.total;
    });
  }, [activePhotoGroup, photos, facesOption]);

  useEffect(() => {
    if (activePhotoGroup) {
      onVirtualPageViewTrack({
        pageViewTitle: `Create a Card - Select a photo from: ${activePhotoGroup.label}`,
        pageViewPath: `/element/photo/select/${activePhotoGroup.label}`,
      });
    }
  }, [activePhotoGroup]);

  useEffect(() => {
    if (!total) {
      return;
    }

    if (
      facesOption === PHOTO_SELECTION_CONTROL_FACES_OPTIONS.INDIVIDUAL &&
      !hasPhotoWithIndividualFace
    ) {
      setFacesOption(PHOTO_SELECTION_CONTROL_FACES_OPTIONS.ALL);
    }

    if (
      facesOption === PHOTO_SELECTION_CONTROL_FACES_OPTIONS.MULTIPLE &&
      !hasPhotoWithMultipleFaces
    ) {
      setFacesOption(PHOTO_SELECTION_CONTROL_FACES_OPTIONS.ALL);
    }
  }, [total, facesOption]);

  const photoListClasses = classNames({
    'card-photo-selection-control-photo-list': true,
    'single-column': gridType === GRID_TYPES.SINGLE,
    'double-columns': gridType === GRID_TYPES.DOUBLE,
  });

  return (
    <div className="card-element-photo-selection-control-content">
      <div className="card-element-control-header">
        <span className="card-element-control-header-title">{title}</span>
        {allowDismiss && <DismissButton onClick={onClose} />}
      </div>

      {photoGroups.length > 1 && (
        <CardElementPhotoSelectionControlNav
          photoGroups={photoGroups}
          activePhotoGroupIndex={activePhotoGroupIndex}
          onPhotoGroupIndexChange={setActivePhotoGroupIndex}
        />
      )}
      {activePhotoGroup?.id !== 'custom-upload' && (
        <Fragment>
          {!photosContext?.grid_select_disabled && (
            <CardElementPhotoSelectionControlGridSelect
              onGridTypeChange={newGridType => setGridType(newGridType)}
            />
          )}
          <div className="card-photo-selection-control-scroll">
            <div className={photoListClasses} key={activePhotoGroup.id}>
              {availablePhotos.map(record => {
                const photo = record.photo || record;
                const isEntitled =
                  photosContext?.force_entitlement ||
                  typeof photo?.is_entitled_matched_photo === 'undefined' ||
                  !!photo?.is_entitled_matched_photo;
                const isSelected =
                  photo.id === currentSelectedPhoto?.id ||
                  (maxPhotosCount > 1 &&
                    !!selectedPhotos.find(p => p.id === photo.id));

                return (
                  <CardPhotoSelect
                    key={record?.id ?? photo.id}
                    photo={{
                      ...photo,
                      faceblocked_thumbnail_urls:
                        record?.faceblocked_thumbnail_urls,
                    }}
                    onSelect={() => {
                      if (
                        maxPhotosCount > 1 &&
                        selectedPhotos.length === maxPhotosCount &&
                        !selectedPhotos.find(p => p.id === photo.id)
                      ) {
                        return;
                      }

                      if (maxPhotosCount > 1) {
                        setSelectedPhotos(
                          selectedPhotos.find(p => p.id === photo.id)
                            ? selectedPhotos.filter(p => p.id !== photo.id)
                            : [...selectedPhotos, photo],
                        );
                        return;
                      }

                      if (isSelected) {
                        setCurrentSelectedPhoto(null);
                        setCurrentSelectedRecord(null);
                      } else {
                        setCurrentSelectedPhoto(photo);
                        setCurrentSelectedRecord(record);
                      }
                    }}
                    selected={isSelected}
                    isEntitled={isEntitled}
                    isSingleGrid={gridType === GRID_TYPES.SINGLE}
                    watermarkSettings={watermarkSettings}
                  />
                );
              })}
            </div>
          </div>
        </Fragment>
      )}
      {activePhotoGroup?.id === 'custom-upload' && !loading && (
        <CardElementPhotoControlAssetUpload
          onUploaded={assetImage => {
            setCurrentSelectedPhoto(assetImage);
            setCurrentSelectedRecord(null);
            onClose();
            onChange?.(assetImage);
          }}
        />
      )}
      {loadMore && photos.length < total && (
        <div className="card-photo-selection-control-photo-list-actions">
          <Button
            btnStyle="link"
            className="mb-2"
            onClick={async () => {
              setLoadingMore(true);
              await loadMore();
              setLoadingMore(false);
            }}
            btnBlock
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}

      <div className="card-element-control-footer">
        <Button
          btnBlock
          onClick={() => {
            onClose();
            if (maxPhotosCount > 1) {
              onChange?.(selectedPhotos);
            } else {
              onChange?.(currentSelectedPhoto);
              onRecordChange?.(currentSelectedRecord);
            }
          }}
          disabled={
            (maxPhotosCount === 1 && !currentSelectedPhoto) ||
            (maxPhotosCount > 1 &&
              ((!maxPhotosCountStrict && !selectedPhotos?.length) ||
                (maxPhotosCountStrict &&
                  selectedPhotos?.length !== maxPhotosCount)))
          }
        >
          Done
        </Button>
      </div>
    </div>
  );
};

CardElementPhotoSelectionControl.propTypes = propTypes;

export const CARD_ELEMENT_PHOTO_SELECTION_CONTROL_MODAL =
  'CARD_ELEMENT_PHOTO_SELECTION_CONTROL_MODAL';

/**
 * Here's a priority list so it's easier to determine what photos are expected based off params.
 *
 * 1. PRO Access to Member Matches
 * This is now first priority, used for PRO access to the Member Matches in order to re-order.
 *
 * 2. AlbumMembershipGroupIdentity (Matches) photos
 * Second priority, since it ties up the most to joined albums.
 *
 * 3. AlbumMembershipGroup (all) photos
 * Third priority, ties with joined albums when there is no identity to care for (i.e customPrints)
 *
 * 4.Album (all) photos
 * Fourth priority. This should ALWAYS be passed with is_all_photos = true. Limits the scope to an album [HACK]
 *
 * 5. Roster Identities
 * Fifth priority. Used by Waldograms, where you can select a Roster Identity to send a Waldogram to.
 *
 * 6. User Photos
 *  Final fallback. Mainly used for the shop, where there's no album or membership to talk about.
 */
const withCandidatePhotosForProAccessIdentity = graphql(
  IdentityAlbumFolderPhotos,
  {
    options: ({ photosContext }) => ({
      variables: {
        id: photosContext?.identity_id,
        album_folder_id: photosContext?.album_folder_id,
      },
    }),
    props: ({
      data: { identity, fetchMore, loading } = {},
      ownProps: {
        mappings = [
          {
            id: 'matches',
            label: 'Member Matches',
            path: 'matched_photos_extended',
          },
        ],
      },
    }) => {
      return {
        loading,
        defaultPhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: identity,
          mappings,
          fetchMore,
        }),
      };
    },
    skip: ({ photosContext, teamPhotos }) => {
      if (teamPhotos?.length) {
        return true;
      }
      return (
        !photosContext?.is_pro_access ||
        typeof photosContext?.is_album_membership_group === 'undefined' ||
        !photosContext?.is_album_membership_group ||
        !photosContext?.identity_id
      );
    },
  },
);

const withCandidatePhotosFromMembershipGroup = graphql(
  getAccountCardMembershipGroupConfigurationPhotosQuery,
  {
    options: ({ photosContext }) => ({
      variables: {
        album_membership_group_id:
          photosContext?.album_folder_id ?? photosContext?.album_id,
      },
    }),
    props: ({
      data: { albumMembershipGroup, fetchMore, loading } = {},
      ownProps: {
        mappings = [
          {
            id: 'allPhotos',
            label: 'All Photos',
            path: 'all_photos',
          },
          {
            id: 'matches',
            label: 'Matches',
            path: 'matched_photos_extended',
          },
          {
            id: 'favorites',
            label: 'Favorites',
            path: 'favorited_photos',
          },
        ],
      },
    }) => {
      return {
        loading,
        defaultPhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: albumMembershipGroup,
          mappings,
          fetchMore,
        }),
      };
    },
    skip: ({ photosContext, teamPhotos }) => {
      if (teamPhotos?.length) {
        return true;
      }
      return (
        photosContext?.is_pro_access ||
        typeof photosContext?.is_album_membership_group === 'undefined' ||
        !photosContext?.is_album_membership_group ||
        photosContext?.identity_id
      );
    },
  },
);

const withCandidatePhotosFromMembershipGroupIdentity = graphql(
  getAccountCardMembershipGroupIdentityConfigurationPhotosQuery,
  {
    options: ({ photosContext }) => ({
      variables: {
        album_membership_group_id:
          photosContext?.root_album_folder_id ??
          photosContext?.album_folder_id ??
          photosContext?.album_id,
        identity_id: photosContext?.identity_id,
      },
    }),
    props: ({
      data: { albumMembershipGroup, fetchMore, loading } = {},
      ownProps: {
        mappings = [
          {
            id: 'allPhotos',
            label: 'All Photos',
            path: 'all_photos',
          },
          {
            id: 'matches',
            label: 'Matches',
            path: 'identities[0].matched_photos_extended',
          },
          {
            id: 'favorites',
            label: 'Favorites',
            path: 'favorited_photos',
          },
        ],
      },
    }) => {
      return {
        loading,
        defaultPhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: albumMembershipGroup,
          mappings,
          fetchMore,
        }),
      };
    },
    skip: ({ photosContext, teamPhotos }) => {
      if (teamPhotos?.length) {
        return true;
      }
      return (
        photosContext?.is_pro_access ||
        typeof photosContext?.is_album_membership_group === 'undefined' ||
        !photosContext?.is_album_membership_group ||
        !photosContext?.identity_id
      );
    },
  },
);

const withCandidatePhotosFromAlbum = graphql(
  getAccountCardAlbumConfigurationPhotosQuery,
  {
    options: ({ photosContext }) => ({
      variables: {
        album_id: photosContext?.album_id,
      },
    }),
    props: ({
      data: { album, fetchMore, loading } = {},
      ownProps: {
        mappings = [
          {
            id: 'photos',
            label: 'All Photos',
            path: 'photos',
          },
          {
            id: 'favorites',
            label: 'Favorites',
            path: 'favorited_photos',
          },
        ],
      },
    }) => {
      return {
        loading,
        defaultPhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: album,
          mappings,
          fetchMore,
        }),
      };
    },
    skip: ({ photosContext, teamPhotos }) => {
      if (teamPhotos?.length) {
        return true;
      }
      // Skip the query if it involves RosterIdentities
      return (
        !photosContext?.album_id ||
        !photosContext?.is_all_photos ||
        photosContext?.is_album_membership_group
      );
    },
  },
);

const withCandidatePhotosFromUser = graphql(
  getAccountCardConfigurationPhotosQuery,
  {
    options: ({ photosContext }) => ({
      variables: {
        identity_ids: photosContext?.identity_id
          ? [photosContext?.identity_id]
          : undefined,
        album_folder_id: photosContext?.album_folder_id
          ? photosContext?.album_folder_id
          : undefined,
        album_ids: photosContext?.album_id
          ? [photosContext?.album_id]
          : undefined,
        hierarchy_item_id: photosContext?.album_folder_id
          ? photosContext?.album_folder_id
          : photosContext?.album_id,
      },
    }),
    props: ({
      data: { me, fetchMore, loading } = {},
      ownProps: {
        mappings = [
          {
            id: 'matches',
            label: 'Matches',
            path: 'matched_photos',
          },
          {
            id: 'favorites',
            label: 'Favorites',
            path: 'favorited_photos',
          },
        ],
      },
    }) => {
      return {
        loading,
        defaultPhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: me,
          mappings,
          fetchMore,
        }),
      };
    },
    skip: ({ photosContext, teamPhotos }) => {
      if (teamPhotos?.length) {
        return true;
      }
      // Skip the query if it involves RosterIdentities
      return (
        photosContext?.is_pro_access ||
        photosContext?.roster_identity_ids?.length > 0 ||
        photosContext?.is_album_membership_group ||
        photosContext?.is_all_photos
      );
    },
  },
);

// TODO [cards] replace this with a new super query that
//  handles Account, Identity, SelectedIdentity, and RosterIdentity level photos.
const withCandidatePhotosFromRosterIdentities = graphql(
  getAccountCardRosterIdentityConfigurationPhotosQuery,
  {
    options: ({ photosContext }) => {
      return {
        variables: {
          roster_identity_ids: photosContext.roster_identity_ids,
        },
      };
    },
    props: ({
      data: { me, fetchMore, loading } = {},
      ownProps: {
        mappings = [
          {
            id: 'matches',
            label: 'Matches',
            path: 'roster_identities.records[0].photos.matched_photos',
          },
          {
            id: 'faceHighlight',
            label: 'Spotlight',
            path: 'roster_identities.records[0].photos.face_highlighted_photos',
          },
          {
            id: 'favorites',
            label: 'Favorites',
            path: 'roster_identities.records[0].photos.favorited_photos',
          },
          {
            id: 'allPhotos',
            label: 'All Photos',
            path: 'roster_identities.records[0].photos.all_photos',
          },
        ],
      },
    }) => {
      return {
        loading,
        defaultPhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: me,
          mappings,
          fetchMore,
        }),
      };
    },
    skip: ({ photosContext, teamPhotos }) => {
      if (teamPhotos?.length) {
        return true;
      }
      // Skip the query if it doesn't involve RosterIdentities.
      return (
        !photosContext?.roster_identity_ids?.length ||
        photosContext?.is_album_membership_group ||
        photosContext?.is_all_photos
      );
    },
  },
);

export default withModal(
  compose(
    withCandidatePhotosForProAccessIdentity,
    withCandidatePhotosFromUser,
    withCandidatePhotosFromAlbum,
    withCandidatePhotosFromRosterIdentities,
    withCandidatePhotosFromMembershipGroup,
    withCandidatePhotosFromMembershipGroupIdentity,
  )(CardElementPhotoSelectionControl),
  {
    name: CARD_ELEMENT_PHOTO_SELECTION_CONTROL_MODAL,
    dismissible: false,
    size: 'sm',
    type: 'full',
    hideHeader: true,
    shouldCloseOnOverlayClick: false,
    className: 'card-element-photo-selection-modal modal-floating-panel',
  },
);
